import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Page,
  Layout,
  Card,
  TextField,
  Select,
  Checkbox,
  ChoiceList,
  RangeSlider,
  Banner,
  BlockStack,
  InlineStack,
  Text,
  Tabs,
  Badge,
  Spinner,
  Box,
  Divider,
} from '@shopify/polaris';
import { SaveIcon } from '@shopify/polaris-icons';

import { useStore } from '../providers/StoreProvider';
import { api } from '../utils/api';
import AppLayout from '../components/AppLayout';
import BarPreview, { formatCountdown } from '../components/BarPreview';
import ColorPicker from '../components/ColorPicker';
import PremiumFeatureLock from '../components/PremiumFeatureLock';
import CountryMultiSelect from '../components/CountryMultiSelect';
import TierList, {
  tierRowsFromBar,
  toPayloadTiers,
  toRenderableTiers,
  toThresholdNumber,
  validateTiers,
} from '../components/TierList';

// Maps ISO currency code -> display symbol
const CURRENCY_SYMBOLS = {
  USD: '$', EUR: '€', GBP: '£', JPY: '¥', CAD: 'C$', AUD: 'A$',
  CHF: 'CHF', CNY: '¥', INR: '₹', BRL: 'R$', MXN: 'MX$', KRW: '₩',
  SGD: 'S$', HKD: 'HK$', SEK: 'kr', NOK: 'kr', DKK: 'kr', NZD: 'NZ$',
  ZAR: 'R', AED: 'AED', SAR: 'SAR', THB: '฿', MYR: 'RM', IDR: 'Rp',
  PHP: '₱', TWD: 'NT$', PLN: 'zł', CZK: 'Kč', HUF: 'Ft',
};

function getCurrencySymbol(code) {
  return (code && CURRENCY_SYMBOLS[code]) || code || '$';
}

const COMMON_CURRENCIES = [
  { label: 'US Dollar (USD)', value: 'USD' },
  { label: 'Euro (EUR)', value: 'EUR' },
  { label: 'British Pound (GBP)', value: 'GBP' },
  { label: 'Japanese Yen (JPY)', value: 'JPY' },
  { label: 'Canadian Dollar (CAD)', value: 'CAD' },
  { label: 'Australian Dollar (AUD)', value: 'AUD' },
  { label: 'Swiss Franc (CHF)', value: 'CHF' },
  { label: 'Chinese Yuan (CNY)', value: 'CNY' },
  { label: 'Indian Rupee (INR)', value: 'INR' },
  { label: 'Brazilian Real (BRL)', value: 'BRL' },
  { label: 'Mexican Peso (MXN)', value: 'MXN' },
  { label: 'South Korean Won (KRW)', value: 'KRW' },
  { label: 'Singapore Dollar (SGD)', value: 'SGD' },
  { label: 'Hong Kong Dollar (HKD)', value: 'HKD' },
  { label: 'Swedish Krona (SEK)', value: 'SEK' },
  { label: 'Norwegian Krone (NOK)', value: 'NOK' },
  { label: 'Danish Krone (DKK)', value: 'DKK' },
  { label: 'New Zealand Dollar (NZD)', value: 'NZD' },
  { label: 'South African Rand (ZAR)', value: 'ZAR' },
  { label: 'UAE Dirham (AED)', value: 'AED' },
  { label: 'Saudi Riyal (SAR)', value: 'SAR' },
  { label: 'Thai Baht (THB)', value: 'THB' },
  { label: 'Malaysian Ringgit (MYR)', value: 'MYR' },
  { label: 'Indonesian Rupiah (IDR)', value: 'IDR' },
  { label: 'Philippine Peso (PHP)', value: 'PHP' },
  { label: 'Polish Zloty (PLN)', value: 'PLN' },
  { label: 'Czech Koruna (CZK)', value: 'CZK' },
];

const DEFAULT_BAR = {
  name: 'New Shipping Bar',
  is_active: true,
  free_shipping_threshold: 50,
  target_currency: null,
  use_exchange_rate: true,
  custom_exchange_rate: null,
  position: 'top_sticky',
  display_delay: 0,
  // eslint-disable-next-line no-template-curly-in-string
  initial_message: '🚚 Free shipping on orders over ${threshold}!',
  // eslint-disable-next-line no-template-curly-in-string
  progress_message: '🛒 Add ${remaining} more to get FREE SHIPPING!',
  goal_reached_message: '🎉 Congratulations! You\'ve earned FREE SHIPPING!',
  background_color: '#000000',
  text_color: '#FFFFFF',
  progress_bar_color: '#4CAF50',
  progress_bar_bg_color: '#333333',
  font_family: 'inherit',
  font_size: '16px',
  font_weight: '500',
  padding: '12px 20px',
  border_radius: '7px',
  bar_height: '48px',
  show_progress_bar: true,
  progress_bar_height: '4px',
  progress_bar_position: 'bottom',
  progress_bar_animated: true,
  link_url: '',
  link_open_new_tab: true,
  device_targeting: 'all',
  page_targeting_enabled: false,
  show_on_pages: [],
  hide_on_pages: [],
  show_close_button: true,
  close_button_color: '#FFFFFF',
  remember_closed: false,
  remember_closed_duration: 24,
  // Premium features
  background_image_url: '',
  background_image_opacity: 1,
  geo_targeting_enabled: false,
  geo_target_countries: [],
  auto_currency_enabled: false,
  auto_language_enabled: false,
  target_languages: [],
  scheduling_enabled: false,
  schedule_start: null,
  schedule_end: null,
  // Countdown - a Basic plan feature (Q1), so it sits with the base fields
  // rather than with the premium ones above.
  countdown_enabled: false,
  countdown_end: null,
  countdown_zero_behavior: 'hide',
  countdown_expired_message: '',
  // In-cart placement (Premium, Q2). Auto-detection defaults on (H4) - it is
  // the merchant's off switch, not something they have to find and turn on.
  in_cart_enabled: false,
  in_cart_mode: 'in_addition',
  in_cart_selector: '',
  in_cart_auto_detect: true,
};

const POSITION_OPTIONS = [
  { label: 'Top', value: 'top' },
  { label: 'Bottom', value: 'bottom' },
  { label: 'Top (Sticky)', value: 'top_sticky' },
  { label: 'Bottom (Sticky)', value: 'bottom_sticky' },
];

const DEVICE_OPTIONS = [
  { label: 'All Devices', value: 'all' },
  { label: 'Desktop Only', value: 'desktop' },
  { label: 'Mobile Only', value: 'mobile' },
  { label: 'Tablet Only', value: 'tablet' },
];

const FONT_OPTIONS = [
  { label: 'Inherit from theme', value: 'inherit' },
  { label: 'Arial', value: 'Arial, sans-serif' },
  { label: 'Helvetica', value: 'Helvetica, sans-serif' },
  { label: 'Georgia', value: 'Georgia, serif' },
  { label: 'Times New Roman', value: '"Times New Roman", serif' },
  { label: 'Verdana', value: 'Verdana, sans-serif' },
];

const FONT_SIZE_OPTIONS = [
  { label: '12px', value: '12px' },
  { label: '14px', value: '14px' },
  { label: '16px', value: '16px' },
  { label: '18px', value: '18px' },
  { label: '20px', value: '20px' },
];

const FONT_WEIGHT_OPTIONS = [
  { label: 'Normal', value: 'normal' },
  { label: 'Bold', value: 'bold' },
  { label: 'Light', value: '300' },
  { label: 'Medium', value: '500' },
];

const TARGET_LANGUAGES = [
  { label: 'Spanish', value: 'es' },
  { label: 'French', value: 'fr' },
  { label: 'German', value: 'de' },
  { label: 'Italian', value: 'it' },
  { label: 'Portuguese', value: 'pt' },
  { label: 'Dutch', value: 'nl' },
  { label: 'Russian', value: 'ru' },
  { label: 'Arabic', value: 'ar' },
  { label: 'Japanese', value: 'ja' },
  { label: 'Chinese (Simplified)', value: 'zh' },
  { label: 'Korean', value: 'ko' },
  { label: 'Hindi', value: 'hi' },
  { label: 'Turkish', value: 'tr' },
  { label: 'Polish', value: 'pl' },
  { label: 'Swedish', value: 'sv' },
];

// Q7: what `${earned}` names once a shopper has cleared more than one tier.
// Values match `tier_stacking_policy` on the model.
const TIER_STACKING_OPTIONS = [
  { label: 'Name every reward they have cleared', value: 'all_cleared' },
  { label: 'Name only the highest reward they have cleared', value: 'highest_only' },
];

// The largest delay setTimeout can hold without overflowing to "fire now".
const MAX_TIMEOUT_MS = 2147483647;

// CD-3: what the bar does at zero. Values match `countdown_zero_behavior` on
// the model. The bar applies this itself rather than waiting for the server to
// stop serving it, because the storefront config is edge cached.
const COUNTDOWN_ZERO_OPTIONS = [
  { label: 'Hide the bar', value: 'hide' },
  { label: 'Show a message', value: 'message' },
];

// CD-3: "Show a message" with nothing to show is the one countdown combination
// that ships the defect the zero behaviour exists to prevent. The storefront
// script falls back to the bar's own message when `expiredMessage` is blank,
// and that message may itself carry ${countdown} - which then renders a frozen
// 00:00:00, exactly what the Select's own help text promises never happens.
//
// The script keeps its fallback for a payload that somehow arrives blank; this
// stops the merchant saving the combination in the first place.
const COUNTDOWN_EXPIRED_MESSAGE_REQUIRED =
  'Enter the message the bar shows at zero, or switch the behaviour to "Hide the bar". Left empty the bar keeps its own message, and a message that counts down would sit frozen at 00:00:00.';

// IC-1: where the bar renders once in-cart placement is on. Values match
// `in_cart_mode` on the model.
const IN_CART_MODE_OPTIONS = [
  { label: 'In addition to the announcement bar', value: 'in_addition' },
  { label: 'Instead of the announcement bar', value: 'instead_of' },
];

// S-7: which of BarPreview's two renderings the preview panel shows. This is a
// view control over the editor's one preview, not bar configuration - nothing
// here is saved.
//
// It is deliberately merchant-driven rather than switched automatically off
// `in_cart_enabled`: under `in_addition` BOTH placements render on the real
// storefront at once, so an automatic switch would have to pick one of the two
// and would be wrong about the other. A toggle also lets a merchant look at the
// in-cart rendering while in-cart placement is still off and they are deciding
// whether to turn it on.
const PREVIEW_PLACEMENT_OPTIONS = [
  { label: 'Announcement bar', value: 'announcement' },
  { label: 'In cart', value: 'in_cart' },
];

// The mount point the storefront script looks for first, before a merchant
// selector and before auto-detection. Q4 chose curated auto-detection *with*
// this manual fallback rather than auto-detection alone: every app in the
// researched field fails at automatic in-drawer placement, so the snippet is
// the only placement that works on any theme, and it is a required part of the
// build rather than a nicety.
const IN_CART_SNIPPET = '<div id="jambofy-cart-bar"></div>';

// M2 / S-6: the themes the curated selector list in
// `backend/static/js/shipping-bar.js` was verified by hand on. One constant so
// there is one line to edit.
//
// PROVISIONAL: the manual theme checklist has not been run yet. This has to be
// corrected to the themes auto-detection actually passed on before the PR, or
// the editor tells merchants something nobody verified.
const AUTO_DETECT_VERIFIED_THEMES = 'Dawn and Craft';

// IC-4 / AC-16: what the storefront reported about finding a mount point, keyed
// by `in_cart_detection_method`. These five keys are the same five enforced by
// `IN_CART_DETECTION_METHODS` in `backend/app/routes/api.py` and commented on
// the model column - all three lists move together.
//
// `auto` is deliberately not a success tone. The script guessed at the
// merchant's cart drawer, and the only person who can confirm the guess is the
// merchant looking at their own storefront (H4): the failure mode is "found the
// wrong element", not only "found nothing".
const IN_CART_DETECTION_STATUS = {
  mount_point: {
    label: 'Mount point found',
    tone: 'success',
    detail:
      'Your storefront found the snippet you pasted into your theme, and the bar renders inside it.',
  },
  selector: {
    label: 'Custom selector matched',
    tone: 'success',
    detail:
      'Your storefront matched the CSS selector you supplied, and the bar renders inside that element.',
  },
  auto: {
    label: 'Auto-detected: verify on your storefront',
    tone: 'attention',
    detail:
      'We recognised something that looks like your cart drawer, but we guessed. Open your own storefront, add an item to the cart, and check the bar sits where you want it. If it does not, paste the snippet below where it should go.',
  },
  unstable: {
    label: 'Detected, but your theme rebuilds the cart faster than we can re-attach',
    tone: 'warning',
    detail:
      'Your theme rebuilt the cart repeatedly and we stopped re-inserting the bar rather than fight it. Paste the snippet below into your cart drawer: a mount point you place yourself survives a rebuild.',
  },
  none: {
    label: 'Not detected',
    tone: 'critical',
    detail:
      'Your storefront found nowhere to put the bar. Paste the snippet below into your cart drawer or cart template.',
  },
};

// A bar nothing has been reported about yet. The same wording as `none`,
// because it is the same fact - but not in red, because nothing has failed and
// nothing has been looked at.
const IN_CART_DETECTION_UNREPORTED = {
  label: 'Not detected',
  tone: undefined,
  detail:
    'No shopper has loaded a page with this bar since in-cart placement was switched on, so your storefront has not reported back yet.',
};

/**
 * The instant a detection report was written, as a Date.
 *
 * `in_cart_detected_at` comes off `to_dict()` as a naive UTC ISO string, and is
 * deliberately not in the service's `_INSTANT_FIELDS` - it is an observation,
 * not a wall-clock the merchant set. JS parses a date-time with no timezone
 * designator as *local* time, so reading it straight would misdate the report
 * by the merchant's UTC offset. Appending the designator is the whole job.
 *
 * Exported for its own test: the bug it prevents is invisible in a UTC runner.
 */
export function parseDetectionInstant(value) {
  if (!value) return null;
  const hasZone = /(?:Z|[+-]\d{2}:?\d{2})$/.test(value);
  const parsed = new Date(hasZone ? value : `${value}Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
}

// The message placeholders, as literals. They are written out here rather than
// inline so the `no-template-curly-in-string` disable lives in one place.
// eslint-disable-next-line no-template-curly-in-string
const PLACEHOLDER_THRESHOLD = '${threshold}';
// eslint-disable-next-line no-template-curly-in-string
const PLACEHOLDER_REMAINING = '${remaining}';
// eslint-disable-next-line no-template-curly-in-string
const PLACEHOLDER_EARNED = '${earned}';
// eslint-disable-next-line no-template-curly-in-string
const PLACEHOLDER_COUNTDOWN = '${countdown}';

/**
 * The time left on the configured countdown, in the format the bar itself
 * renders - `formatCountdown` is BarPreview's, so there is one format and not
 * two.
 *
 * Its own component, and so its own one-second interval, because the tick has
 * to re-render this one line rather than the whole editor. The interval is
 * cleared on unmount and stopped at zero (CD-4).
 *
 * The instant is parsed in the admin browser's timezone while the server reads
 * the same string in the bar's own zone, so this is a sanity check on what the
 * merchant typed and not the clock the shopper sees: the storefront gets an
 * absolute instant from the server. The field's help text names the zone that
 * actually applies.
 */
function CountdownRemaining({ endMs }) {
  const [nowMs, setNowMs] = useState(() => Date.now());

  useEffect(() => {
    const startedAt = Date.now();
    setNowMs(startedAt);
    if (startedAt >= endMs) return undefined;

    const intervalId = setInterval(() => {
      const now = Date.now();
      setNowMs(now);
      if (now >= endMs) clearInterval(intervalId);
    }, 1000);

    return () => clearInterval(intervalId);
  }, [endMs]);

  if (nowMs >= endMs) {
    return (
      <Text variant="bodySm" tone="critical">
        This end time has already passed, so the countdown is finished.
      </Text>
    );
  }

  return (
    <Text variant="bodySm" tone="subdued">
      {`Time left right now: ${formatCountdown(endMs - nowMs)}`}
    </Text>
  );
}

function BarEditor() {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = !!id;

  const { store, getBar, createBar, updateBar, isPremium } = useStore();
  const storeCurrency = store?.shop_currency || 'USD';
  const storeCurrencySymbol = getCurrencySymbol(storeCurrency);

  const [bar, setBar] = useState(DEFAULT_BAR);
  const [isLoading, setIsLoading] = useState(isEditing);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTab, setSelectedTab] = useState(0);
  const [hasChanges, setHasChanges] = useState(false);
  const [liveRate, setLiveRate] = useState(null);
  const [rateLoading, setRateLoading] = useState(false);
  // Local raw string for the custom rate input - avoids controlled-number truncating '0.002' → '0'
  const [customRateStr, setCustomRateStr] = useState('');
  const customRateSynced = useRef(false);
  // Language codes the merchant wants to translate the bar messages into
  const [selectedLanguageCodes, setSelectedLanguageCodes] = useState([]);
  // The goal ladder. Row 1 is not a stored tier - it is the bar's own
  // free_shipping_threshold / progress_message, which handleTiersChange keeps
  // in sync so the preview and the payload read one value, not two.
  const [tierRows, setTierRows] = useState(() => tierRowsFromBar(DEFAULT_BAR));
  // Simulated cart value for the preview. null means "follow the bar" - it
  // tracks tier 1 until the merchant drags the slider (S-3).
  const [previewCartOverride, setPreviewCartOverride] = useState(null);
  // Which rendering the preview shows (S-7). Announcement is the default
  // because it is the placement every bar has; in-cart is the opt-in one.
  const [previewVariant, setPreviewVariant] = useState('announcement');

  // Load bar if editing
  useEffect(() => {
    const loadBar = async () => {
      if (!isEditing) return;

      const result = await getBar(id);
      if (result.success) {
        setBar(result.bar);
        setTierRows(tierRowsFromBar(result.bar));
      } else {
        setError('Failed to load bar');
      }
      setIsLoading(false);
    };

    loadBar();
  }, [id, isEditing, getBar]);

  // Fetch live exchange rate when target currency changes
  useEffect(() => {
    const targetCurrency = bar.target_currency;
    if (!targetCurrency || targetCurrency === storeCurrency) {
      setLiveRate(null);
      return;
    }
    if (!bar.use_exchange_rate) return;

    let cancelled = false;
    setRateLoading(true);
    api.get('/api/exchange-rates', { base: storeCurrency })
      .then((data) => {
        if (!cancelled && data?.rates) {
          setLiveRate(data.rates[targetCurrency] ?? null);
        }
      })
      .catch(() => {})
      .finally(() => { if (!cancelled) setRateLoading(false); });

    return () => { cancelled = true; };
  }, [bar.target_currency, bar.use_exchange_rate, storeCurrency]);

  // Sync customRateStr once when bar loads with an existing rate (edit mode)
  useEffect(() => {
    if (!customRateSynced.current && bar.custom_exchange_rate != null) {
      setCustomRateStr(String(bar.custom_exchange_rate));
      customRateSynced.current = true;
    }
  }, [bar.custom_exchange_rate]);

  // Sync selected language codes from loaded bar translations (edit mode)
  useEffect(() => {
    const langs = (bar.target_languages || []).map(t => t.lang).filter(Boolean);
    if (langs.length > 0) {
      setSelectedLanguageCodes(langs);
    }
  }, [bar.target_languages]);

  // Update field
  const updateField = useCallback((field, value) => {
    setBar(prev => ({ ...prev, [field]: value }));
    setHasChanges(true);
  }, []);

  // Tier rows changed. Row 1 mirrors straight back onto the bar's own two
  // columns, so BarPreview, the currency conversion and the save payload all
  // keep reading `free_shipping_threshold` / `progress_message` exactly as they
  // did before tiers existed.
  const handleTiersChange = useCallback((nextRows) => {
    setTierRows(nextRows);
    const first = nextRows[0];
    if (first) {
      setBar(prev => ({
        ...prev,
        free_shipping_threshold: toThresholdNumber(first.threshold) ?? 0,
        progress_message: first.message,
      }));
    }
    setHasChanges(true);
  }, []);

  // S-5: a bar that already runs to a schedule end is almost always running the
  // same campaign, so switching the countdown on proposes that instant instead
  // of an empty field. Only ever a default - it fills a blank field once and
  // never overwrites a value the merchant chose.
  const handleCountdownEnabledChange = useCallback((enabled) => {
    setBar((prev) => {
      const next = { ...prev, countdown_enabled: enabled };
      if (enabled && !prev.countdown_end && prev.scheduling_enabled && prev.schedule_end) {
        next.countdown_end = prev.schedule_end;
      }
      return next;
    });
    setHasChanges(true);
  }, []);

  const tierValidation = useMemo(() => validateTiers(tierRows), [tierRows]);
  const hasMultipleTiers = tierRows.length > 1;

  // --- Countdown -----------------------------------------------------------
  // The configured end as an absolute ms value, or null while the countdown is
  // off or the merchant has not typed a parseable instant yet.
  const countdownEndMs = useMemo(() => {
    if (!bar.countdown_enabled || !bar.countdown_end) return null;
    const parsed = new Date(bar.countdown_end).getTime();
    return Number.isNaN(parsed) ? null : parsed;
  }, [bar.countdown_enabled, bar.countdown_end]);

  // BarPreview renders nothing at all for an elapsed countdown whose zero
  // behaviour is "hide" (CD-3), so the editor has to say why the panel is empty
  // rather than leave the merchant looking at a blank box. One timeout, fired
  // at the instant the countdown reaches zero, rather than a second per-second
  // interval alongside the preview's own.
  const [countdownNowMs, setCountdownNowMs] = useState(() => Date.now());

  useEffect(() => {
    if (countdownEndMs == null) return undefined;

    const now = Date.now();
    setCountdownNowMs(now);
    const msToZero = countdownEndMs - now;
    // A delay past the 32-bit limit overflows and fires immediately, so a
    // campaign more than ~24 days out is simply not scheduled - it cannot
    // reach zero inside one editing session anyway.
    if (msToZero <= 0 || msToZero > MAX_TIMEOUT_MS) return undefined;

    const timeoutId = setTimeout(() => setCountdownNowMs(Date.now()), msToZero + 250);
    return () => clearTimeout(timeoutId);
  }, [countdownEndMs]);

  const countdownZeroBehavior = bar.countdown_zero_behavior || 'hide';

  // CD-3: the expired message is required whenever the bar is going to show one
  // - see COUNTDOWN_EXPIRED_MESSAGE_REQUIRED. Null when the field is fine, the
  // message to show when it is not, so it feeds the TextField's `error` and the
  // save guard from one place, the way `validateTiers` does for the ladder.
  //
  // Gated on `countdown_enabled` because a bar with the countdown off never
  // reads this field and must not be blocked by it. Deliberately *not* also
  // gated on `countdown_end`: "message means a message" is one rule a merchant
  // can hold, and the field is on screen with the error against it either way.
  //
  // Whitespace only counts as blank. The storefront's fallback is a plain
  // truthiness check (`shipping-bar.js`, the `expiredMessage &&` guard), so a
  // single space does not restore the bar's own copy - it swaps a blank string
  // in and the shopper reads an empty bar. Different failure, same cause, and
  // neither is a message a merchant meant to write.
  const countdownExpiredMessageError = useMemo(() => {
    if (!bar.countdown_enabled) return null;
    if (countdownZeroBehavior !== 'message') return null;
    if ((bar.countdown_expired_message || '').trim()) return null;
    return COUNTDOWN_EXPIRED_MESSAGE_REQUIRED;
  }, [bar.countdown_enabled, bar.countdown_expired_message, countdownZeroBehavior]);

  const countdownExpired = countdownEndMs != null && countdownNowMs >= countdownEndMs;
  const previewHiddenByCountdown = countdownExpired && countdownZeroBehavior !== 'message';

  // The zone the value in the countdown field is read in, and the three cases
  // are genuinely different sentences.
  //
  // A saved bar carries its own `effective_timezone`, which may be an override
  // and not the store's. A bar that has never been saved carries none, and
  // borrows the store's `shop_timezone` off the store payload the editor
  // already holds - `store` is null until that fetch resolves, hence the
  // optional chaining. `shop_timezone` is emitted raw, so null means "no zone
  // is known" rather than UTC, and the generic copy stays as the third branch:
  // a shopper-facing clock is not somewhere to guess.
  const barTimezone = bar.effective_timezone || null;
  const storeTimezone = store?.shop_timezone || null;

  let countdownTimezoneHelp =
    "Read in your store's timezone. Save this bar to see the exact zone named here.";
  if (barTimezone) {
    countdownTimezoneHelp = `Read in ${barTimezone}, this bar's timezone. Every shopper counts down to that same moment, wherever they are.`;
  } else if (storeTimezone) {
    countdownTimezoneHelp = `Read in ${storeTimezone}, your store's timezone, which this bar follows. Every shopper counts down to that same moment, wherever they are.`;
  }

  // --- In-cart placement ---------------------------------------------------
  // A bar whose column is null reads as the model's default rather than as an
  // empty Select or an unchecked box, so what the merchant is shown is what the
  // storefront would do. handleSave writes these back rather than the nulls.
  const inCartMode = bar.in_cart_mode || 'in_addition';
  const inCartAutoDetect = bar.in_cart_auto_detect !== false;

  const inCartStatus =
    IN_CART_DETECTION_STATUS[bar.in_cart_detection_method] ||
    IN_CART_DETECTION_UNREPORTED;
  const inCartDetectedAt = parseDetectionInstant(bar.in_cart_detected_at);

  // Save bar
  const handleSave = useCallback(async () => {
    // S-4: mirror the server's ascending/distinct rule rather than spending a
    // round trip on a 400 the merchant cannot see the cause of.
    if (!tierValidation.isValid) {
      setSelectedTab(0);
      setError(
        tierValidation.formError ||
          'Fix the highlighted goal tier fields before saving.'
      );
      return;
    }

    // CD-3: same shape as the tier rule above - block on the client rather than
    // let a bar save into the one state that renders a frozen 00:00:00. There
    // is no server-side mirror of this one: a blank expired message is a valid
    // payload the script has a fallback for, and the point is that a merchant
    // never chooses it.
    if (countdownExpiredMessageError) {
      setSelectedTab(0);
      setError('Add the message this bar shows when the countdown reaches zero before saving.');
      return;
    }

    setIsSaving(true);
    setError(null);

    // Include selected language codes so the backend can auto-translate
    const payload = { ...bar, selected_language_codes: selectedLanguageCodes };

    // Save the zero behaviour the Select is showing. A bar whose column is
    // null displays as "Hide the bar", and writing that null straight back
    // would leave the stored value disagreeing with what the merchant read.
    payload.countdown_zero_behavior = countdownZeroBehavior;

    // Same for the two in-cart values that fall back to a default when the
    // column is null - but only when the payload carries the in-cart block at
    // all. A non-premium store's bar comes back from
    // `to_dict(include_premium=False)` without these keys, and adding them here
    // would have a downgraded store's save silently rewrite in-cart
    // configuration it cannot see.
    if ('in_cart_enabled' in payload) {
      payload.in_cart_mode = inCartMode;
      payload.in_cart_auto_detect = inCartAutoDetect;
    }

    // `tiers` is whole or absent. More than one row sends the complete ordered
    // list; one row on a bar that has stored rows sends [] to clear them; one
    // row on a bar that never had any omits the key entirely, so a
    // single-threshold bar's payload is the one `master` sends (MT-5).
    //
    // A non-premium store never sends the key at all (Q6): the tier editor is
    // read-only for it, and a downgraded store whose bar still has stored rows
    // has to be able to save the rest of the editor without every save coming
    // back 403 "multi_tier".
    if (isPremium && tierRows.length > 1) {
      payload.tiers = toPayloadTiers(tierRows);
    } else if (isPremium && Array.isArray(bar.tiers) && bar.tiers.length > 0) {
      payload.tiers = [];
    } else {
      delete payload.tiers;
    }

    const result = isEditing
      ? await updateBar(id, payload)
      : await createBar(payload);

    setIsSaving(false);

    if (result.success) {
      setHasChanges(false);
      // Redirect to dashboard after save/create
      navigate('/app');
    } else {
      // Handle different error formats
      let errorMessage = 'Failed to save bar';
      if (result.error?.upgrade_required) {
        errorMessage = `Premium feature required: ${result.error.restricted_features?.join(', ')}`;
      } else if (result.error?.error) {
        // Error from backend JSON: { error: "message" }
        errorMessage = result.error.error;
      } else if (result.error?.message) {
        errorMessage = result.error.message;
      } else if (typeof result.error === 'string') {
        errorMessage = result.error;
      }
      setError(errorMessage);
    }
  }, [
    bar,
    tierRows,
    tierValidation,
    countdownZeroBehavior,
    countdownExpiredMessageError,
    inCartMode,
    inCartAutoDetect,
    isPremium,
    selectedLanguageCodes,
    isEditing,
    id,
    createBar,
    updateBar,
    navigate,
  ]);

  // Effective currency symbol: use target currency if set, else fall back to store currency
  const effectiveCurrencySymbol = (bar.target_currency && bar.target_currency !== storeCurrency)
    ? getCurrencySymbol(bar.target_currency)
    : storeCurrencySymbol;

  // Effective exchange rate: live rate or custom rate, only when target currency differs from store
  const hasTargetCurrency = bar.target_currency && bar.target_currency !== storeCurrency;
  const effectiveRate = hasTargetCurrency
    ? (bar.use_exchange_rate ? liveRate : bar.custom_exchange_rate)
    : null;
  // Threshold converted to the display currency for preview (falls back to raw if no rate yet)
  const effectiveThreshold = effectiveRate
    ? bar.free_shipping_threshold * effectiveRate
    : bar.free_shipping_threshold;

  // Whether merchant has a fixed manual display currency set (mutually exclusive with auto-convert)
  const hasManualCurrency = !!(bar.target_currency && bar.target_currency !== storeCurrency);

  // --- Preview -------------------------------------------------------------
  // The tier list as the storefront would receive it: tier 1 carries the
  // "Free shipping" reward label the wire payload gives it, so the preview's
  // `${earned}` names the same rewards the real bar names.
  //
  // Blanked for a non-premium store because `to_frontend_config` blanks it
  // too - a downgraded store's stored rows are still in the editor but its
  // live bar renders as a single-threshold bar, and the preview has to agree
  // with the bar rather than with the database.
  const previewTiers = useMemo(
    () => (isPremium ? toRenderableTiers(tierRows) : []),
    [isPremium, tierRows]
  );

  // MT-4: one rate for the whole ladder. `effectiveThreshold` is tier 1 in the
  // display currency, so the ratio it implies converts every other tier -
  // the same thing BarPreview does internally.
  const conversionRatio = bar.free_shipping_threshold > 0
    ? effectiveThreshold / bar.free_shipping_threshold
    : 1;

  const displayTierThresholds = previewTiers.map(
    (tier) => tier.threshold * conversionRatio
  );

  const highestDisplayThreshold = displayTierThresholds.length > 0
    ? Math.max(...displayTierThresholds)
    : effectiveThreshold;

  // S-3: the slider runs past the last goal so the all-cleared state is
  // reachable. The floor keeps the slider usable on a bar with a 0 threshold.
  const previewMax = Math.max(10, Math.ceil((highestDisplayThreshold || 0) * 1.2));
  const previewStep = Math.max(1, Math.round(previewMax / 100));

  // Until the merchant drags, the simulated cart sits halfway to the first
  // goal - the state today's fixed preview showed.
  const previewCartValue = Math.min(
    previewMax,
    Math.max(
      0,
      previewCartOverride == null
        ? Math.round((effectiveThreshold || 0) / 2)
        : previewCartOverride
    )
  );

  const previewStateLabel = (() => {
    if (previewCartValue === 0) return 'Empty cart: the bar shows your initial message.';
    if (displayTierThresholds.length === 0) {
      return previewCartValue >= effectiveThreshold
        ? 'Goal reached: the bar shows your goal-reached message.'
        : 'Below the goal: the bar shows your progress message.';
    }
    const activeIndex = displayTierThresholds.findIndex(
      (threshold) => threshold > previewCartValue
    );
    return activeIndex === -1
      ? `All ${displayTierThresholds.length} goals cleared: the bar shows your goal-reached message.`
      : `Working toward tier ${activeIndex + 1} of ${displayTierThresholds.length}.`;
  })();

  // S-7: say what the merchant is looking at, and - when they are previewing a
  // placement that is switched off - that no shopper is seeing it yet. Without
  // that line the in-cart preview reads as confirmation the feature is live.
  const previewPlacementHelpText = (() => {
    if (previewVariant !== 'in_cart') {
      return 'The sitewide bar, at the position you chose on the Style tab.';
    }
    return bar.in_cart_enabled
      ? 'Inline in your cart, sized to whatever it is mounted in, with no close button.'
      : 'In-cart placement is switched off, so shoppers do not see this yet. Turn it on under Style → In-Cart Placement.';
  })();

  const tabs = [
    { id: 'content', content: 'Content' },
    { id: 'style', content: 'Style' },
    { id: 'targeting', content: 'Targeting' },
    { id: 'advanced', content: 'Advanced' },
  ];

  if (isLoading) {
    return (
      <AppLayout>
        <Page title="Edit Bar">
          <Box padding="800">
            <InlineStack align="center">
              <Spinner size="large" />
            </InlineStack>
          </Box>
        </Page>
      </AppLayout>
    );
  }

  return (
    <AppLayout>
      <Page
        title={isEditing ? `Edit: ${bar.name}` : 'Create New Bar'}
        backAction={{
          content: 'Back',
          onAction: () => navigate('/app'),
        }}
        primaryAction={{
          content: 'Save',
          icon: SaveIcon,
          onAction: handleSave,
          loading: isSaving,
          disabled: !hasChanges,
        }}
      >
        {error && (
          <Box paddingBlockEnd="400">
            <Banner
              title="Error"
              tone="critical"
              onDismiss={() => setError(null)}
            >
              <p>{error}</p>
            </Banner>
          </Box>
        )}

        <Layout>
          {/* Editor Panel */}
          <Layout.Section>
            <Card>
              <Tabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}>
                <Box paddingBlockStart="400">
                  {/* Content Tab */}
                  {selectedTab === 0 && (
                    <BlockStack gap="400">
                      <TextField
                        label="Bar Name"
                        value={bar.name}
                        onChange={(value) => updateField('name', value)}
                        helpText="Internal name for identification"
                      />

                      <Divider />

                      <Text variant="headingSm">Goal Tiers</Text>
                      <Text variant="bodySm" tone="subdued">
                        Tier 1 is your free shipping goal. Add tiers at higher cart values
                        to keep a shopper climbing after they have earned it.
                      </Text>

                      {/* MT-7 / AC-8: the app advertises a reward, it does not
                          create or apply one. This is the failure mode that
                          generates the category's most damaging reviews, and
                          tiers multiply it by the number of tiers. */}
                      <Banner tone="info" title="You set the rewards up in Shopify">
                        <p>
                          This bar advertises each tier&apos;s reward; it does not create or
                          apply it. Set the free shipping rate, the gift or the discount up in
                          Shopify yourself, or a shopper will be promised something they never
                          receive.
                        </p>
                        <p>
                          Discount codes especially: the bar reads the cart total{' '}
                          <strong>before</strong> a discount code is applied at checkout, so a
                          shopper using one can be shown as having cleared a tier their
                          discounted total no longer reaches.
                        </p>
                      </Banner>

                      {hasMultipleTiers && (
                        <Select
                          label="When a shopper has cleared more than one goal"
                          options={TIER_STACKING_OPTIONS}
                          value={bar.tier_stacking_policy || 'all_cleared'}
                          onChange={(value) => updateField('tier_stacking_policy', value)}
                          helpText={`Sets what ${PLACEHOLDER_EARNED} lists in your messages.`}
                        />
                      )}

                      {/* H3: auto-translation covers the three bar-level
                          messages only. A merchant who adds a second tier would
                          otherwise silently lose a Premium feature. */}
                      {hasMultipleTiers && bar.auto_language_enabled && (
                        <Banner tone="warning" title="Tier text is not auto-translated">
                          <p>
                            Auto language conversion translates the initial, progress and
                            goal-reached messages only. Tier messages and reward labels are
                            <strong> not </strong>
                            translated; every shopper reads them exactly as you write them
                            here. On a bar with more than one tier the shopper reads the
                            tier&apos;s own message rather than the translated progress
                            message.
                          </p>
                          <p>
                            If your shoppers read more than one language, write your tier
                            messages and reward labels in a language they read.
                          </p>
                        </Banner>
                      )}

                      {/* AC-7: rows 2-5 and "Add tier" sit inside
                          PremiumFeatureLock, which needs `isPremium` as well as
                          `feature` or it renders as if every store were on the
                          free plan. `isPremium` here is useStore()'s, the same
                          `Store.is_premium()` that `store.is_premium` carries,
                          and the same value the four other locks on this page
                          are given. */}
                      <TierList
                        tiers={tierRows}
                        onChange={handleTiersChange}
                        isPremium={isPremium}
                        currencyCode={storeCurrency}
                      />

                      <Divider />

                      <Text variant="headingSm">Messages</Text>
                      <Text variant="bodySm" tone="subdued">
                        {`Use ${PLACEHOLDER_THRESHOLD} for the goal amount and ${PLACEHOLDER_REMAINING} for the amount still to go. On a bar with more than one tier, ${PLACEHOLDER_EARNED} lists the rewards the shopper has already cleared.`}
                      </Text>

                      <TextField
                        label="Initial Message"
                        value={bar.initial_message}
                        onChange={(value) => updateField('initial_message', value)}
                        multiline={2}
                        helpText="Shown when cart is empty"
                      />

                      <TextField
                        label="Goal Reached Message"
                        value={bar.goal_reached_message}
                        onChange={(value) => updateField('goal_reached_message', value)}
                        multiline={2}
                        helpText={
                          hasMultipleTiers
                            ? 'Shown once every tier has been cleared'
                            : 'Shown when threshold is met'
                        }
                      />

                      <Divider />

                      {/* CD-1: the countdown ships on the free plan (Q1), so
                          it is deliberately not wrapped in
                          PremiumFeatureLock the way scheduling below it is. */}
                      <Text variant="headingSm">Countdown</Text>
                      <Text variant="bodySm" tone="subdued">
                        {`Put ${PLACEHOLDER_COUNTDOWN} in any message above to show the time left in this campaign. It ticks once a second as HH:MM:SS, and as 2d 04:30:00 while more than a day is left.`}
                      </Text>

                      <Checkbox
                        label="Show a countdown on this bar"
                        checked={!!bar.countdown_enabled}
                        onChange={handleCountdownEnabledChange}
                      />

                      {bar.countdown_enabled && (
                        <>
                          <TextField
                            label="Countdown ends"
                            type="datetime-local"
                            value={bar.countdown_end || ''}
                            onChange={(value) => updateField('countdown_end', value)}
                            helpText={countdownTimezoneHelp}
                          />

                          {countdownEndMs != null && (
                            <CountdownRemaining endMs={countdownEndMs} />
                          )}

                          {!bar.countdown_end && (
                            <Banner tone="warning">
                              <p>
                                Set the date and time this campaign ends. Until you do, no
                                countdown runs on your bar.
                              </p>
                            </Banner>
                          )}

                          <Select
                            label="When the countdown reaches zero"
                            options={COUNTDOWN_ZERO_OPTIONS}
                            value={countdownZeroBehavior}
                            onChange={(value) => updateField('countdown_zero_behavior', value)}
                            helpText="The bar applies this itself, so it never sits at 00:00:00 while a shopper's browser is showing a cached copy."
                          />

                          {countdownZeroBehavior === 'message' && (
                            <TextField
                              label="Expired message"
                              value={bar.countdown_expired_message || ''}
                              onChange={(value) => updateField('countdown_expired_message', value)}
                              multiline={2}
                              placeholder="This offer has ended."
                              requiredIndicator
                              error={countdownExpiredMessageError}
                              helpText="Replaces the bar's message once the countdown reaches zero. Required, because the message it replaces may be the one counting down."
                            />
                          )}
                        </>
                      )}

                      <Divider />

                      <Text variant="headingSm">Link (Optional)</Text>

                      <TextField
                        label="Link URL"
                        value={bar.link_url || ''}
                        onChange={(value) => updateField('link_url', value)}
                        placeholder="https://..."
                        helpText="Click destination for the bar"
                      />

                      <Checkbox
                        label="Open link in new tab"
                        checked={bar.link_open_new_tab}
                        onChange={(value) => updateField('link_open_new_tab', value)}
                      />
                    </BlockStack>
                  )}

                  {/* Style Tab */}
                  {selectedTab === 1 && (
                    <BlockStack gap="400">
                      <Select
                        label="Position"
                        options={POSITION_OPTIONS}
                        value={bar.position}
                        onChange={(value) => updateField('position', value)}
                      />

                      <RangeSlider
                        label="Bar Height"
                        value={parseInt(bar.bar_height, 10) || 48}
                        onChange={(value) => updateField('bar_height', `${value}px`)}
                        output
                        min={40}
                        max={100}
                        step={1}
                        suffix="px"
                        helpText="Minimum 40px. Taller bars accommodate the inset progress bar comfortably."
                      />

                      <Divider />

                      {/* In-cart placement (Premium, Q2). It sits with
                          Position rather than with the other premium blocks on
                          the Advanced tab because it is the same question -
                          where the bar shows - and because a free merchant has
                          to come across the lock to know the feature exists.

                          `isPremium` is useStore()'s, the same
                          `Store.is_premium()` the other six locks on this page
                          are given and the same value the backend gates
                          `in_cart_placement` on. Both props are required:
                          without `isPremium` the lock renders as if every
                          merchant were free. */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="In-Cart Placement"
                      >
                        <Text variant="headingSm">In-Cart Placement</Text>
                        <Text variant="bodySm" tone="subdued">
                          Show this bar inside your cart drawer or cart page, where a shopper
                          is deciding whether to add one more item.
                        </Text>

                        <Checkbox
                          label="Show this bar in the cart"
                          checked={!!bar.in_cart_enabled}
                          onChange={(value) => updateField('in_cart_enabled', value)}
                          disabled={!isPremium}
                        />

                        {bar.in_cart_enabled && (
                          <>
                            <Select
                              label="Where the bar appears"
                              options={IN_CART_MODE_OPTIONS}
                              value={inCartMode}
                              onChange={(value) => updateField('in_cart_mode', value)}
                            />

                            {/* IC-1: "instead of" has no fallback placement.
                                If nothing in the cart is matched the shopper
                                sees no bar at all, which is a silent failure
                                unless it is said here. */}
                            {inCartMode === 'instead_of' && (
                              <Banner tone="warning" title="This bar has nowhere else to show">
                                <p>
                                  Instead of the announcement bar, this bar renders only where
                                  a mount point is found in the cart. If your theme is never
                                  matched, shoppers see no bar at all; paste the snippet below
                                  into your cart drawer to guarantee one.
                                </p>
                              </Banner>
                            )}

                            {/* IC-4 / AC-16: what the storefront reported, not
                                what we assert. */}
                            <Box
                              background="bg-surface-secondary"
                              padding="300"
                              borderRadius="100"
                            >
                              <BlockStack gap="200">
                                <InlineStack gap="200" blockAlign="center">
                                  <Text variant="bodySm" fontWeight="semibold">
                                    Detection status
                                  </Text>
                                  <Badge tone={inCartStatus.tone}>{inCartStatus.label}</Badge>
                                </InlineStack>

                                <Text variant="bodySm" tone="subdued">
                                  {inCartStatus.detail}
                                </Text>

                                {inCartDetectedAt && (
                                  <Text variant="bodySm" tone="subdued">
                                    {`Last reported ${inCartDetectedAt.toLocaleString(undefined, {
                                      dateStyle: 'medium',
                                      timeStyle: 'short',
                                    })}.`}
                                  </Text>
                                )}

                                {/* The honest framing of an open endpoint that
                                    overwrites state: this is a report from the
                                    last shopper page view, not a live probe of
                                    the theme, and it changes only when a
                                    shopper loads a page. */}
                                <Text variant="bodySm" tone="subdued">
                                  This is what your storefront reported on the last shopper page
                                  view, not a live check of your theme. It changes the next time
                                  a shopper loads a page with this bar.
                                </Text>
                              </BlockStack>
                            </Box>

                            {/* H4: auto-detection is on by default and this is
                                the merchant's off switch, for a theme where the
                                guess lands somewhere they do not want. */}
                            <Checkbox
                              label="Automatically detect my cart drawer"
                              checked={inCartAutoDetect}
                              onChange={(value) => updateField('in_cart_auto_detect', value)}
                              helpText={`Verified on ${AUTO_DETECT_VERIFIED_THEMES}. Detection is only ever tried after the snippet and your own selector, so it can never override a placement you chose.`}
                            />

                            <TextField
                              label="Cart drawer CSS selector (optional)"
                              value={bar.in_cart_selector || ''}
                              onChange={(value) => updateField('in_cart_selector', value)}
                              placeholder=".cart-drawer__footer"
                              autoComplete="off"
                              helpText="Checked after the snippet below and before automatic detection. The bar is inserted at the top of the first element that matches."
                            />

                            {/* Q4's manual path, and the only placement that
                                works on any theme. */}
                            <Box
                              background="bg-surface-secondary"
                              padding="300"
                              borderRadius="100"
                            >
                              <BlockStack gap="200">
                                <Text variant="bodySm" fontWeight="semibold">
                                  Place the bar yourself
                                </Text>
                                <Text variant="bodySm" tone="subdued">
                                  Paste this line into your theme&apos;s cart drawer or cart
                                  template (Online store → Themes → Edit code →{' '}
                                  <code>cart-drawer.liquid</code> or{' '}
                                  <code>main-cart.liquid</code>) wherever you want the bar to
                                  sit.
                                </Text>
                                <TextField
                                  label="Mount point snippet"
                                  value={IN_CART_SNIPPET}
                                  readOnly
                                  selectTextOnFocus
                                  monospaced
                                  autoComplete="off"
                                />
                              </BlockStack>
                            </Box>
                          </>
                        )}
                      </PremiumFeatureLock>

                      <Divider />

                      <Text variant="headingSm">Colors</Text>

                      <InlineStack gap="400">
                        <Box minWidth="150px">
                          <ColorPicker
                            label="Background"
                            color={bar.background_color}
                            onChange={(color) => updateField('background_color', color)}
                          />
                        </Box>
                        <Box minWidth="150px">
                          <ColorPicker
                            label="Text"
                            color={bar.text_color}
                            onChange={(color) => updateField('text_color', color)}
                          />
                        </Box>
                      </InlineStack>

                      <InlineStack gap="400">
                        <Box minWidth="150px">
                          <ColorPicker
                            label="Progress Bar"
                            color={bar.progress_bar_color}
                            onChange={(color) => updateField('progress_bar_color', color)}
                          />
                        </Box>
                        <Box minWidth="150px">
                          <ColorPicker
                            label="Progress Background"
                            color={bar.progress_bar_bg_color}
                            onChange={(color) => updateField('progress_bar_bg_color', color)}
                          />
                        </Box>
                      </InlineStack>

                      <Divider />

                      <Text variant="headingSm">Typography</Text>

                      <Select
                        label="Font Family"
                        options={FONT_OPTIONS}
                        value={bar.font_family}
                        onChange={(value) => updateField('font_family', value)}
                      />

                      <InlineStack gap="400">
                        <Box minWidth="150px">
                          <Select
                            label="Font Size"
                            options={FONT_SIZE_OPTIONS}
                            value={bar.font_size}
                            onChange={(value) => updateField('font_size', value)}
                          />
                        </Box>
                        <Box minWidth="150px">
                          <Select
                            label="Font Weight"
                            options={FONT_WEIGHT_OPTIONS}
                            value={bar.font_weight}
                            onChange={(value) => updateField('font_weight', value)}
                          />
                        </Box>
                      </InlineStack>

                      <Divider />

                      <Text variant="headingSm">Progress Bar</Text>

                      <Checkbox
                        label="Show progress bar"
                        checked={bar.show_progress_bar}
                        onChange={(value) => updateField('show_progress_bar', value)}
                      />

                      {bar.show_progress_bar && (
                        <>
                          <TextField
                            label="Progress Bar Height"
                            value={bar.progress_bar_height}
                            onChange={(value) => updateField('progress_bar_height', value)}
                            placeholder="4px"
                          />

                          <Select
                            label="Progress Bar Position"
                            options={[
                              { label: 'Bottom of bar', value: 'bottom' },
                              { label: 'Top of bar', value: 'top' },
                            ]}
                            value={bar.progress_bar_position}
                            onChange={(value) => updateField('progress_bar_position', value)}
                          />

                          <Checkbox
                            label="Animated progress bar"
                            checked={bar.progress_bar_animated}
                            onChange={(value) => updateField('progress_bar_animated', value)}
                          />
                        </>
                      )}

                      <Divider />

                      <Text variant="headingSm">Close Button</Text>

                      <Checkbox
                        label="Show close button"
                        checked={bar.show_close_button}
                        onChange={(value) => updateField('show_close_button', value)}
                      />

                      {bar.show_close_button && (
                        <>
                          <ColorPicker
                            label="Close Button Color"
                            color={bar.close_button_color}
                            onChange={(color) => updateField('close_button_color', color)}
                          />

                          <Checkbox
                            label="Remember when closed"
                            checked={bar.remember_closed}
                            onChange={(value) => updateField('remember_closed', value)}
                          />

                          {bar.remember_closed && (
                            <TextField
                              label="Remember duration (hours)"
                              type="number"
                              value={String(bar.remember_closed_duration)}
                              onChange={(value) => updateField('remember_closed_duration', parseInt(value) || 24)}
                            />
                          )}
                        </>
                      )}

                      <Divider />

                      {/* Premium Feature: Background Image */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Background Images"
                      >
                        <Text variant="headingSm">Background Image</Text>

                        <TextField
                          label="Image URL"
                          value={bar.background_image_url || ''}
                          onChange={(value) => updateField('background_image_url', value)}
                          placeholder="https://..."
                          disabled={!isPremium}
                        />

                        <RangeSlider
                          label="Image Opacity"
                          value={bar.background_image_opacity * 100}
                          onChange={(value) => updateField('background_image_opacity', value / 100)}
                          output
                          min={0}
                          max={100}
                          disabled={!isPremium}
                        />
                      </PremiumFeatureLock>
                    </BlockStack>
                  )}

                  {/* Targeting Tab */}
                  {selectedTab === 2 && (
                    <BlockStack gap="400">
                      <Text variant="headingSm">Device Targeting</Text>

                      <Select
                        label="Show on devices"
                        options={DEVICE_OPTIONS}
                        value={bar.device_targeting}
                        onChange={(value) => updateField('device_targeting', value)}
                      />

                      <Divider />

                      <Text variant="headingSm">Page Targeting</Text>

                      <Checkbox
                        label="Enable page targeting"
                        checked={bar.page_targeting_enabled}
                        onChange={(value) => updateField('page_targeting_enabled', value)}
                      />

                      {bar.page_targeting_enabled && (
                        <>
                          <TextField
                            label="Show only on pages"
                            value={(bar.show_on_pages || []).join('\n')}
                            onChange={(value) => updateField('show_on_pages', value.split('\n').filter(p => p.trim()))}
                            multiline={3}
                            helpText="Enter page paths, one per line (e.g., /collections/*, /products/*)"
                          />

                          <TextField
                            label="Hide on pages"
                            value={(bar.hide_on_pages || []).join('\n')}
                            onChange={(value) => updateField('hide_on_pages', value.split('\n').filter(p => p.trim()))}
                            multiline={3}
                            helpText="Enter page paths to exclude, one per line"
                          />
                        </>
                      )}

                      <Divider />

                      {/* Premium Feature: Geo Targeting */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Geo Targeting"
                      >
                        <Text variant="headingSm">Geo Targeting (Shopify Markets)</Text>

                        <Checkbox
                          label="Enable geo targeting"
                          checked={bar.geo_targeting_enabled}
                          onChange={(value) => updateField('geo_targeting_enabled', value)}
                          disabled={!isPremium}
                        />

                        {bar.geo_targeting_enabled && isPremium && (
                          <CountryMultiSelect
                            selected={bar.geo_target_countries || []}
                            onChange={(next) => updateField('geo_target_countries', next)}
                          />
                        )}
                      </PremiumFeatureLock>
                    </BlockStack>
                  )}

                  {/* Advanced Tab */}
                  {selectedTab === 3 && (
                    <BlockStack gap="400">
                      <TextField
                        label="Display Delay (ms)"
                        type="number"
                        value={String(bar.display_delay)}
                        onChange={(value) => updateField('display_delay', parseInt(value) || 0)}
                        helpText="Wait before showing the bar"
                      />

                      <TextField
                        label="Custom Padding"
                        value={bar.padding}
                        onChange={(value) => updateField('padding', value)}
                        placeholder="10px 20px"
                      />

                      <TextField
                        label="Border Radius"
                        value={bar.border_radius}
                        onChange={(value) => updateField('border_radius', value)}
                        placeholder="0px"
                      />

                      <Divider />

                      {/* Premium Feature: Bar Currency Display */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Bar Currency Display"
                      >
                        <Text variant="headingSm">Bar Currency Display</Text>
                        <Text variant="bodySm" tone="subdued">
                          Show the free shipping threshold in a fixed target currency on your bar
                        </Text>

                        {bar.auto_currency_enabled && (
                          <Banner tone="info">
                            Disable "Auto Currency Conversion" below to use a fixed display currency.
                          </Banner>
                        )}

                        <Select
                          label="Display currency"
                          options={[
                            { label: `Store default (${storeCurrency})`, value: '' },
                            ...COMMON_CURRENCIES.filter(c => c.value !== storeCurrency),
                          ]}
                          value={bar.target_currency || ''}
                          onChange={(value) => {
                            updateField('target_currency', value || null);
                            if (!value) {
                              updateField('custom_exchange_rate', null);
                              setCustomRateStr('');
                            }
                          }}
                          disabled={!isPremium || bar.auto_currency_enabled}
                        />

                        {isPremium && !bar.auto_currency_enabled && bar.target_currency && bar.target_currency !== storeCurrency && (
                          <BlockStack gap="300">
                            <Select
                              label="Exchange rate source"
                              options={[
                                { label: 'Use live international exchange rate', value: 'live' },
                                { label: 'Set my own custom rate', value: 'custom' },
                              ]}
                              value={bar.use_exchange_rate ? 'live' : 'custom'}
                              onChange={(value) => updateField('use_exchange_rate', value === 'live')}
                            />

                            {bar.use_exchange_rate ? (
                              <Box
                                background="bg-surface-secondary"
                                padding="300"
                                borderRadius="100"
                              >
                                {rateLoading ? (
                                  <InlineStack gap="200" align="start">
                                    <Spinner size="small" />
                                    <Text variant="bodySm" tone="subdued">Fetching live rate…</Text>
                                  </InlineStack>
                                ) : liveRate ? (
                                  <BlockStack gap="100">
                                    <Text variant="bodySm">
                                      Live rate: 1 {storeCurrency} = {liveRate.toFixed(4)} {bar.target_currency}
                                    </Text>
                                    <Text variant="bodySm" tone="subdued">
                                      Threshold ≈ {getCurrencySymbol(bar.target_currency)}{(bar.free_shipping_threshold * liveRate).toFixed(2)} {bar.target_currency}
                                    </Text>
                                  </BlockStack>
                                ) : (
                                  <Text variant="bodySm" tone="critical">
                                    Could not fetch live rate. Try saving and reloading, or use a custom rate.
                                  </Text>
                                )}
                              </Box>
                            ) : (
                              <BlockStack gap="200">
                                <TextField
                                  label={`Custom rate: 1 ${storeCurrency} = ? ${bar.target_currency}`}
                                  value={customRateStr}
                                  onChange={(value) => {
                                    setCustomRateStr(value);
                                    if (value === '') {
                                      updateField('custom_exchange_rate', null);
                                    } else {
                                      const parsed = parseFloat(value);
                                      if (!isNaN(parsed)) {
                                        updateField('custom_exchange_rate', parsed);
                                      }
                                    }
                                  }}
                                  placeholder="e.g. 0.92"
                                  inputMode="decimal"
                                  helpText="How many units of the target currency equal 1 unit of your store currency"
                                />
                                {bar.custom_exchange_rate > 0 && (
                                  <Text variant="bodySm" tone="subdued">
                                    Threshold ≈ {getCurrencySymbol(bar.target_currency)}{(bar.free_shipping_threshold * bar.custom_exchange_rate).toFixed(2)} {bar.target_currency}
                                  </Text>
                                )}
                              </BlockStack>
                            )}
                          </BlockStack>
                        )}
                      </PremiumFeatureLock>

                      <Divider />

                      {/* Premium Feature: Auto Currency Conversion */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Auto Currency Conversion"
                      >
                        <Text variant="headingSm">Auto Currency Conversion</Text>
                        <Text variant="bodySm" tone="subdued">
                          Automatically display the threshold in the visitor's active Shopify currency.
                          Uses the exchange rate Shopify Markets already provides - no extra setup required.
                        </Text>

                        {hasManualCurrency && (
                          <Banner tone="info">
                            Set "Bar Currency Display" above back to store default to enable auto conversion.
                          </Banner>
                        )}

                        <Checkbox
                          label="Enable auto currency conversion"
                          checked={bar.auto_currency_enabled}
                          onChange={(value) => updateField('auto_currency_enabled', value)}
                          disabled={!isPremium || hasManualCurrency}
                        />

                        {isPremium && bar.auto_currency_enabled && (
                          <Box
                            background="bg-surface-secondary"
                            padding="300"
                            borderRadius="100"
                          >
                            <BlockStack gap="100">
                              <Text variant="bodySm" fontWeight="semibold">How it works</Text>
                              <Text variant="bodySm" tone="subdued">
                                When a shopper views your store in a different currency via Shopify Markets,
                                the bar reads <code>window.Shopify.currency.rate</code> - the same rate
                                Shopify uses to convert product prices - and applies it to your threshold
                                automatically. No external API calls, always in sync with Shopify.
                              </Text>
                            </BlockStack>
                          </Box>
                        )}
                      </PremiumFeatureLock>

                      <Divider />

                      {/* Premium Feature: Auto Language Conversion */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Auto Language Conversion"
                      >
                        <Text variant="headingSm">Auto Language Conversion</Text>
                        <Text variant="bodySm" tone="subdued">
                          Automatically translate bar messages based on the visitor's browser language.
                          Translations are generated when you save the bar.
                        </Text>

                        <Checkbox
                          label="Enable auto language conversion"
                          checked={bar.auto_language_enabled}
                          onChange={(value) => updateField('auto_language_enabled', value)}
                          disabled={!isPremium}
                        />

                        {isPremium && bar.auto_language_enabled && (
                          <BlockStack gap="300">
                            <ChoiceList
                              allowMultiple
                              title="Languages to translate into"
                              choices={TARGET_LANGUAGES}
                              selected={selectedLanguageCodes}
                              onChange={setSelectedLanguageCodes}
                            />

                            {selectedLanguageCodes.length === 0 && (
                              <Banner tone="warning">
                                Select at least one language to enable translations.
                              </Banner>
                            )}

                            {/* Show which languages already have saved translations */}
                            {(bar.target_languages || []).length > 0 && (
                              <Box
                                background="bg-surface-secondary"
                                padding="300"
                                borderRadius="100"
                              >
                                <BlockStack gap="100">
                                  <Text variant="bodySm" fontWeight="semibold">
                                    Currently translated:
                                  </Text>
                                  <Text variant="bodySm" tone="subdued">
                                    {(bar.target_languages || [])
                                      .map(t => TARGET_LANGUAGES.find(l => l.value === t.lang)?.label || t.lang)
                                      .join(', ')}
                                  </Text>
                                  <Text variant="bodySm" tone="subdued">
                                    Translations will be refreshed on next save.
                                  </Text>
                                </BlockStack>
                              </Box>
                            )}
                          </BlockStack>
                        )}
                      </PremiumFeatureLock>

                      <Divider />

                      {/* Premium Feature: Scheduling */}
                      <PremiumFeatureLock
                        isPremium={isPremium}
                        feature="Auto Scheduling"
                      >
                        <Text variant="headingSm">Scheduling</Text>

                        <Checkbox
                          label="Enable scheduling"
                          checked={bar.scheduling_enabled}
                          onChange={(value) => updateField('scheduling_enabled', value)}
                          disabled={!isPremium}
                        />

                        {bar.scheduling_enabled && isPremium && (
                          <InlineStack gap="400">
                            <TextField
                              label="Start Date"
                              type="datetime-local"
                              value={bar.schedule_start || ''}
                              onChange={(value) => updateField('schedule_start', value)}
                            />
                            <TextField
                              label="End Date"
                              type="datetime-local"
                              value={bar.schedule_end || ''}
                              onChange={(value) => updateField('schedule_end', value)}
                            />
                          </InlineStack>
                        )}
                      </PremiumFeatureLock>
                    </BlockStack>
                  )}
                </Box>
              </Tabs>
            </Card>
          </Layout.Section>

          {/* Preview Panel */}
          <Layout.Section variant="oneThird">
            <Card>
              <BlockStack gap="300">
                <InlineStack align="space-between">
                  <Text variant="headingSm">Preview</Text>
                  <Badge>{bar.is_active ? 'Active' : 'Inactive'}</Badge>
                </InlineStack>

                <Checkbox
                  label="Bar is active"
                  checked={bar.is_active}
                  onChange={(value) => updateField('is_active', value)}
                />

                {/* CD-3: BarPreview renders nothing for an elapsed countdown
                    set to hide, which is the bar behaving correctly - but an
                    empty grey box reads as a broken editor, so say what it is
                    and how to undo it. */}
                {previewHiddenByCountdown ? (
                  <Banner tone="warning" title="Countdown finished, so the bar is hidden">
                    <p>
                      This bar hides itself when the countdown reaches zero, and that time
                      has passed; shoppers see no bar at all. Set a later end time, or
                      switch the zero behaviour to show a message, to keep it on the page.
                    </p>
                  </Banner>
                ) : (
                  <Box
                    background="bg-surface-secondary"
                    padding="400"
                    borderRadius="200"
                  >
                    {/* Every prop below is threaded from live editor state, not
                        from the saved bar: BarPreview only falls back to the
                        payload's own values, which are one save behind whatever
                        the merchant is typing. */}
                    <BarPreview
                      bar={bar}
                      cartValue={previewCartValue}
                      currencySymbol={effectiveCurrencySymbol}
                      effectiveThreshold={effectiveThreshold}
                      tiers={previewTiers}
                      tierStackingPolicy={bar.tier_stacking_policy || 'all_cleared'}
                      countdownEndsAt={bar.countdown_enabled ? bar.countdown_end : undefined}
                      countdownZeroBehavior={countdownZeroBehavior}
                      countdownExpiredMessage={bar.countdown_expired_message || ''}
                      variant={previewVariant}
                    />
                  </Box>
                )}

                {/* S-7: the same bar, rendered the way each placement renders
                    it. `variant` is props-only on BarPreview - in-cart drops
                    the announcement framing and the close button - so this
                    control writes nothing to the bar and nothing to the save
                    payload. */}
                <Select
                  label="Preview placement"
                  options={PREVIEW_PLACEMENT_OPTIONS}
                  value={previewVariant}
                  onChange={setPreviewVariant}
                  helpText={previewPlacementHelpText}
                />

                {/* S-3: one draggable cart value instead of two fixed previews,
                    so every tier state is reachable. */}
                <RangeSlider
                  label="Simulated cart value"
                  value={previewCartValue}
                  onChange={setPreviewCartOverride}
                  min={0}
                  max={previewMax}
                  step={previewStep}
                  output
                  prefix={effectiveCurrencySymbol}
                  helpText={previewStateLabel}
                />

                <Text variant="bodySm" tone="subdued">
                  {`Drag to move a simulated cart from empty to ${effectiveCurrencySymbol}${previewMax} and see each state of the bar.`}
                </Text>
              </BlockStack>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    </AppLayout>
  );
}

export default BarEditor;
